import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';

import { AtlasIcon } from '@crossnokaye/ux-animatics';
import {
  getInitials,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  UserAvatar,
} from '@crossnokaye/ux-common';

import { NavOptions } from '@/components/navigation/constants';
import { AccessSelectorContent } from '@/components/navigation/left/components/shared/access-selector-content';
import { useAgentProxyMode } from '@/hooks/use-agent-proxy-mode';
import { useNavStore } from '@/stores/nav';

import { CollapsedMainNav } from './collapsed-main-nav';
import { FacilityMoreMenu } from './components/facility-more-menu';

/**
 * Collapsed navigation for the left side of the navigation bar.
 */
export const CollapsedNav = ({
  isViewOnly,
  toggleExpanded,
  onSelect,
  onSignOut,
  onToggleActive,
}: {
  isViewOnly: boolean;
  toggleExpanded: () => void;
  onSelect: (id: string) => void;
  onSignOut: () => void;
  onToggleActive: () => void;
}) => {
  const { userName, userAccessIsLoading, enableFacilityMenu, activeNavMenu } = useNavStore();
  const { isAgentProxyMode } = useAgentProxyMode();

  const handleSignOut = () => {
    if (!onSignOut) return;
    onSignOut();
  };

  const handleToggleActive = () => {
    if (!onToggleActive) return;
    onToggleActive();
  };

  const initials = useMemo(() => getInitials(userName ?? ''), [userName]);

  return (
    <TooltipProvider>
      <div
        className={'flex flex-col h-full'}
        data-testid={'collapsed-nav'}
        onClick={toggleExpanded}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={'flex justify-center p-4 cursor-pointer hover:bg-popover'}
              onClick={toggleExpanded}
              data-testid={'collapsed-nav-logo'}
            >
              <AtlasIcon symbol={'atlasLogo'} size={'2rem'} />
            </div>
          </TooltipTrigger>
          <TooltipContent align={'end'} side={'right'}>
            <span className={'text-xs'}>{'Click to Expand Menu'}</span>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger disabled={!enableFacilityMenu} asChild>
            <FacilityMoreMenu />
          </TooltipTrigger>
          <TooltipContent align={'center'} side={'right'}>
            <span className={'text-xs'}>{'Click to View Facility Picker'}</span>
          </TooltipContent>
        </Tooltip>
        {activeNavMenu === NavOptions.Main ? <CollapsedMainNav onSelect={onSelect} /> : null}
        {!isAgentProxyMode && (
          <div
            className={'flex justify-center p-4 align-bottom mt-auto cursor-pointer'}
            onClick={(e) => e.stopPropagation()}
          >
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger data-testid={'collapsed-nav-access-selector'}>
                    {userAccessIsLoading ? (
                      <Loader2 className={'animate-spin h-10 w-10'} />
                    ) : (
                      <UserAvatar
                        initials={initials}
                        indicatorClass={isViewOnly ? 'bg-warning' : 'bg-primary'}
                        className={'bg-popover hover:bg-input'}
                      />
                    )}
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent align={'end'} side={'right'}>
                  <span className={'text-xs'}>{'Click to View Access Options'}</span>
                </TooltipContent>
              </Tooltip>

              <PopoverContent
                side={'top'}
                align={'start'}
                withPortal={true}
                className={'p-0 max-w-max z-sticky'}
                data-testid={'collapsed-nav-access-selector-menu-content'}
              >
                <AccessSelectorContent
                  isViewOnly={isViewOnly}
                  handleToggleActive={handleToggleActive}
                  handleSignOut={handleSignOut}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
